import { apiClient } from '../client';
import type { OwnerType } from '@never2busy/shared';

export interface Keyword {
  id: number;
  ownerType: OwnerType;
  text: string;
  shortText: string | null;
  parentId: number | null;
  categoryId: number | null;
  sort: number;
  flags: unknown;
}

export interface CreateKeywordInput {
  ownerType: OwnerType;
  text: string;
  shortText?: string;
  parentId?: number;
  categoryId?: number;
  sort?: number;
}

export type UpdateKeywordInput = Partial<CreateKeywordInput>;

export interface KeywordCategory {
  id: number;
  ownerType: OwnerType;
  name: string;
  singleSelect: boolean;
  securityScoped: boolean;
  sort: number;
  active: boolean;
}

export interface CreateKeywordCategoryInput {
  ownerType: OwnerType;
  name: string;
  singleSelect?: boolean;
  securityScoped?: boolean;
  sort?: number;
}

export type UpdateKeywordCategoryInput = Partial<
  Omit<CreateKeywordCategoryInput, 'ownerType'>
> & { active?: boolean };

export interface KeywordAccess {
  id: number;
  keywordId: number;
  userId: number;
  user: { id: number; firstName: string; lastName: string; email: string };
}

export const keywordsApi = {
  async list(ownerType?: OwnerType): Promise<Keyword[]> {
    const { data } = await apiClient.get<Keyword[]>('/keywords', {
      params: ownerType ? { ownerType } : undefined,
    });
    return data;
  },
  async forOwner(ownerType: OwnerType, ownerId: number): Promise<Keyword[]> {
    const { data } = await apiClient.get<Keyword[]>('/keywords/for-owner', {
      params: { ownerType, ownerId },
    });
    return data;
  },
  async toggle(
    keywordId: number,
    ownerType: OwnerType,
    ownerId: number,
  ): Promise<{ assigned: boolean }> {
    const { data } = await apiClient.post<{ assigned: boolean }>('/keywords/toggle', {
      keywordId,
      ownerType,
      ownerId,
    });
    return data;
  },
  async create(input: CreateKeywordInput): Promise<Keyword> {
    const { data } = await apiClient.post<Keyword>('/keywords', input);
    return data;
  },
  async update(id: number, input: UpdateKeywordInput): Promise<Keyword> {
    const { data } = await apiClient.patch<Keyword>(`/keywords/${id}`, input);
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/keywords/${id}`);
  },
  // -- Categorías --
  async listCategories(ownerType?: OwnerType): Promise<KeywordCategory[]> {
    const { data } = await apiClient.get<KeywordCategory[]>('/keywords/categories', {
      params: ownerType ? { ownerType } : undefined,
    });
    return data;
  },
  async createCategory(input: CreateKeywordCategoryInput): Promise<KeywordCategory> {
    const { data } = await apiClient.post<KeywordCategory>('/keywords/categories', input);
    return data;
  },
  async updateCategory(id: number, input: UpdateKeywordCategoryInput): Promise<KeywordCategory> {
    const { data } = await apiClient.patch<KeywordCategory>(`/keywords/categories/${id}`, input);
    return data;
  },
  async removeCategory(id: number): Promise<void> {
    await apiClient.delete(`/keywords/categories/${id}`);
  },
  // -- Membresía de etiquetas de seguridad --
  async listAccess(keywordId: number): Promise<KeywordAccess[]> {
    const { data } = await apiClient.get<KeywordAccess[]>(`/keywords/${keywordId}/access`);
    return data;
  },
  async grantAccess(keywordId: number, userId: number): Promise<KeywordAccess> {
    const { data } = await apiClient.post<KeywordAccess>(`/keywords/${keywordId}/access`, {
      userId,
    });
    return data;
  },
  async revokeAccess(accessId: number): Promise<void> {
    await apiClient.delete(`/keywords/access/${accessId}`);
  },
};
